import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';

interface InvoiceData {
  invoiceNumber: string;
  amount: number;
  currency: string;
  createdAt: string;
  taxFields: {
    billingName?: string;
    billingAddress?: string;
    taxAmount?: number;
    gstin?: string;
  };
  metadata: {
    gateway?: string;
    gatewayOrderId?: string;
    orderType?: string;
    planName?: string;
    topUpCredits?: number;
    originalAmount?: number;
    discountAmount?: number;
    couponCode?: string;
  };
}

const InvoiceView: React.FC = () => {
  const { paymentOrderId } = useParams<{ paymentOrderId: string }>();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState<InvoiceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!paymentOrderId) return;
    api.get(`/api/invoices/${paymentOrderId}`)
      .then((response) => setInvoice(response.data.invoice))
      .catch((err) => setError(err?.response?.data?.message || 'Unable to load invoice.'))
      .finally(() => setLoading(false));
  }, [paymentOrderId]);

  const download = async () => {
    try {
      const response = await api.get(`/api/invoices/${paymentOrderId}/download`, { responseType: 'blob' });
      // Server sends a PDF (falls back to HTML only if rendering is unavailable)
      const contentType = response.headers['content-type'] || 'application/pdf';
      const isPdf = contentType.includes('pdf');
      const url = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${paymentOrderId}.${isPdf ? 'pdf' : 'html'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Unable to download invoice.');
    }
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" py={10}><CircularProgress /></Box>;
  }

  if (error || !invoice) {
    return (
      <Stack spacing={2} sx={{ maxWidth: 640, mx: 'auto', px: 2, py: 6 }}>
        <Alert severity="error">{error || 'Invoice not found.'}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/wallet')}>Back to wallet</Button>
      </Stack>
    );
  }

  const itemName = invoice.metadata?.orderType === 'top_up'
    ? `${invoice.metadata.topUpCredits} extra ride unlocks`
    : invoice.metadata?.planName || 'Subscription plan';

  const subtotal = invoice.amount;
  const tax = invoice.taxFields?.taxAmount || 0;

  return (
    <Stack spacing={3} sx={{ maxWidth: 680, mx: 'auto', px: { xs: 2, md: 0 }, py: { xs: 3, md: 5 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
        <Button startIcon={<ArrowBackIcon />} variant="outlined" onClick={() => navigate('/wallet')}>
          Back to wallet
        </Button>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={() => window.print()}
          >
            Print
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<FileDownloadIcon />}
            onClick={download}
          >
            Download
          </Button>
        </Stack>
      </Box>

      <Paper
        variant="outlined"
        id="invoice-print-area"
        sx={{
          p: { xs: 3, md: 4 },
          '@media print': { boxShadow: 'none', border: 'none', p: 0 },
        }}
      >
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap" pb={3} borderBottom="2px solid #ffc928">
          <Box>
            <Box mb={1}>
              <Box component="img" src="/assets/ride-saathi-main-logo.png" alt="RideSaathi" sx={{ height: 42, width: 'auto', display: 'block' }} />
            </Box>
            <Typography variant="h5" fontWeight={900}>Invoice</Typography>
          </Box>
          <Box textAlign={{ xs: 'left', sm: 'right' }}>
            <Typography fontWeight={700} variant="subtitle1">{invoice.invoiceNumber}</Typography>
            <Typography variant="body2" color="text.secondary">
              {new Date(invoice.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}
            </Typography>
          </Box>
        </Box>

        {/* Bill to */}
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>BILLED TO</Typography>
            <Typography fontWeight={700} mt={0.5}>{invoice.taxFields?.billingName || 'Driver'}</Typography>
            {invoice.taxFields?.billingAddress && (
              <Typography variant="body2" color="text.secondary">{invoice.taxFields.billingAddress}</Typography>
            )}
            {invoice.taxFields?.gstin && (
              <Typography variant="body2" color="text.secondary">GSTIN: {invoice.taxFields.gstin}</Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>PAYMENT METHOD</Typography>
            <Typography fontWeight={700} mt={0.5} sx={{ textTransform: 'capitalize' }}>
              {invoice.metadata?.gateway || 'Online'}
            </Typography>
            {invoice.metadata?.gatewayOrderId && (
              <Typography variant="caption" color="text.secondary" display="block">
                Ref: {invoice.metadata.gatewayOrderId}
              </Typography>
            )}
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Line items */}
        <Stack>
          <Box display="flex" justifyContent="space-between" py={1.25}>
            <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ flex: 1 }}>ITEM</Typography>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>AMOUNT</Typography>
          </Box>
          <Divider />
          <Box display="flex" justifyContent="space-between" py={1.75} alignItems="flex-start">
            <Box sx={{ flex: 1 }}>
              <Typography fontWeight={600}>{itemName}</Typography>
              {invoice.metadata?.couponCode && (
                <Typography variant="caption" color="success.main">
                  Coupon: {invoice.metadata.couponCode} (−{invoice.currency} {invoice.metadata.discountAmount?.toFixed(2)})
                </Typography>
              )}
            </Box>
            <Typography fontWeight={600}>{invoice.currency} {subtotal.toFixed(2)}</Typography>
          </Box>
          <Divider />
          <Box display="flex" justifyContent="space-between" py={1.25}>
            <Typography variant="body2" color="text.secondary">Tax (incl. GST)</Typography>
            <Typography variant="body2" color="text.secondary">
              {invoice.currency} {tax.toFixed(2)}
            </Typography>
          </Box>
          <Divider />
          <Box display="flex" justifyContent="space-between" py={2}>
            <Typography variant="h6" fontWeight={900}>Total paid</Typography>
            <Typography variant="h6" fontWeight={900}>{invoice.currency} {subtotal.toFixed(2)}</Typography>
          </Box>
        </Stack>

        <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f7fb', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            This is a computer-generated invoice. For queries, contact support via the Support page.
          </Typography>
        </Box>
      </Paper>
    </Stack>
  );
};

export default InvoiceView;
